import { useMemo, useState } from "react";
import {
  Card,
  Empty,
  Skeleton,
  Tag,
  Typography,
  Alert,
  Tooltip,
} from "antd";
import { ArrowDownOutlined, ArrowUpOutlined } from "@ant-design/icons";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
} from "recharts";
import TradingHouseManageModal from "../TradingHouseManageModal";

const { Title } = Typography;

const PIE_COLORS = [
  "#1e3a8a",
  "#334155",
  "#0ea5e9",
  "#22c55e",
  "#eab308",
  "#f43f5e",
];

function formatAmount(value) {
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));
}

function getSelectedSummary(data, selectedCurrency) {
  if (!data?.currencySummaries?.length) return null;

  if (selectedCurrency) {
    const found = data.currencySummaries.find(
      (item) => item.currency === selectedCurrency
    );
    if (found) return found;
  }

  const cny = data.currencySummaries.find((item) => item.currency === "CNY");
  return cny || data.currencySummaries[0] || null;
}

function getDisplayLabel(item) {
  const key = item?.key || item?.categoryKey;
  const label = item?.label || item?.categoryLabel;

  const labelMap = {
    TO_MAIN_INSTITUTION: "Банк бус руу",
    TO_ANOTHER_INSTITUTION: "Бусад трейдинг хаус руу",
    INSIDE_INSTITUTION: "Байгууллага дотор",
    BY_ALLOCATION_EXPENSE: "Хуваарьт гүйлгээ",
    TOTAL_FEE: "Нийт шимтгэл",
    OTHER: "Бусад",
    MAIN_NON_BANK: "Банк бусаас",
    OTHER_INSTITUTIONS: "Бусад трейдинг хаусаас",
    RETURNED: "Буцаалт",
  };

  return labelMap[key] || label || "-";
}

function SourceSwitcher({ value, onChange }) {
  const options = [
    { key: "report", label: "хийсвэр" },
    { key: "statement", label: "ориг" },
  ];

  return (
    <div className="inline-flex rounded-2xl bg-slate-100 p-1">
      {options.map((option) => {
        const active = value === option.key;

        return (
          <button
            key={option.key}
            type="button"
            onClick={() => onChange(option.key)}
            className={`rounded-xl px-4 py-2 text-sm font-semibold transition-all ${
              active
                ? "bg-white text-slate-900 shadow-sm"
                : "text-slate-500 hover:text-slate-700"
            }`}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

function SummaryCard({ title, amount, currency, type, highlight = false }) {
  const isIncome = type === "income";

  const accent = isIncome
    ? {
        bar: "bg-[#0F9D58]",
        chip: "bg-[#0F9D58] text-white border-[#0F9D58]",
        pill: "border-[#0F9D58] text-[#0F9D58]",
        title: "text-[#0F9D58]",
      }
    : {
        bar: "bg-[#D93025]",
        chip: "bg-[#D93025] text-white border-[#D93025]",
        pill: "border-[#D93025] text-[#D93025]",
        title: "text-[#D93025]",
      };

  return (
    <Card
      className={`overflow-hidden rounded-[20px] border bg-[#FCFCF8] text-[#111111] shadow-none ${
        highlight ? "border-[#111111]" : "border-[#D9D6CF]"
      }`}
      styles={{ body: { padding: 0 } }}
    >
      <div className={`h-1 w-full ${accent.bar}`} />

      <div className="flex flex-col gap-3 p-4">
        <div className="flex items-center justify-between gap-3">
          <div className={`text-[10px] font-semibold uppercase tracking-[0.12em] ${accent.title}`}>
            {title}
          </div>

          <div
            className={`inline-flex rounded-full border px-2.5 py-0.5 text-[10px] font-semibold tracking-[0.08em] ${accent.pill}`}
          >
            {currency || "-"}
          </div>
        </div>

        <div className="text-[26px] font-semibold leading-none tracking-[-0.02em]">
          {formatAmount(amount)}
        </div>

        <div className="flex items-center gap-2">
          <div
            className={`flex h-8 w-8 items-center justify-center rounded-full border ${accent.chip}`}
          >
            {isIncome ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
          </div>

          <div className="text-xs text-[#6B6B6B]">
            {isIncome ? "Орлого" : "Зарлага"}
          </div>
        </div>
      </div>
    </Card>
  );
}

function CustomTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;

  const item = payload[0]?.payload;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white px-3 py-2 shadow-lg">
      <div className="mb-1 text-sm font-semibold text-slate-800">
        {getDisplayLabel(item)}
      </div>
      <div className="text-sm text-slate-600">
        {formatAmount(item?.amount)}
      </div>
      <div className="text-xs text-slate-500">
        {Number(item?.percentage || 0).toFixed(2)}%
      </div>
    </div>
  );
}

function BreakdownLegendList({ items }) {
  return (
    <div className="space-y-3">
      {items.map((item, index) => (
        <div
          key={item.key || item.categoryKey || index}
          className="flex items-start justify-between gap-3"
        >
          <div className="flex min-w-0 items-start gap-2">
            <span
              className="mt-1 h-3 w-3 shrink-0 rounded-full"
              style={{ backgroundColor: PIE_COLORS[index % PIE_COLORS.length] }}
            />
            <div className="text-sm text-slate-700">{getDisplayLabel(item)}</div>
          </div>

          <div className="text-right">
            <div className="text-sm font-semibold text-slate-900">
              {formatAmount(item.amount)}
            </div>
            <div className="text-xs text-slate-500">
              {Number(item.percentage || 0).toFixed(2)}%
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function PieBreakdownCard({ title, items }) {
  const chartData = (items || []).map((item) => ({
    ...item,
    amount: Number(item?.amount || 0),
    percentage: Number(item?.percentage || 0),
    value: Number(item?.amount || 0),
  }));

  const hasData = chartData.some((item) => item.value > 0);

  return (
    <Card
      className="rounded-[28px] border-0 shadow-sm"
      styles={{ body: { padding: 0 } }}
    >
      <div className="border-b border-slate-100 px-5 py-4">
        <div className="text-[18px] font-semibold text-slate-800">{title}</div>
      </div>

      <div className="p-5">
        {!items?.length || !hasData ? (
          <Empty
            description="Мэдээлэл алга"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-[140px_minmax(0,1fr)] md:items-center">
            <div className="mx-auto h-[140px] w-[140px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={chartData}
                    dataKey="value"
                    nameKey="label"
                    innerRadius={38}
                    outerRadius={62}
                    paddingAngle={2}
                    stroke="none"
                  >
                    {chartData.map((entry, index) => (
                      <Cell
                        key={entry.key || entry.categoryKey || index}
                        fill={PIE_COLORS[index % PIE_COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <RechartsTooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            </div>

            <BreakdownLegendList items={chartData} />
          </div>
        )}
      </div>
    </Card>
  );
}

function OverviewSection({
  currency,
  activeSource,
  onSourceChange,
  totalIncome,
  totalExpense,
}) {
  return (
    <Card
      className="rounded-[30px] border-0 shadow-md"
      styles={{ body: { padding: 22 } }}
    >
      <div className="mb-5 flex items-start justify-between gap-4">
        <div>
          <Title level={3} className="!mb-1">
            7 хоногийн тойм
          </Title>
        </div>

        <SourceSwitcher value={activeSource} onChange={onSourceChange} />
      </div>

      <div className="space-y-4">
        <SummaryCard
          title="Нийт орлого"
          amount={totalIncome}
          currency={currency}
          type="income"
          highlight
        />

        <SummaryCard
          title="Нийт зарлага"
          amount={totalExpense}
          currency={currency}
          type="expense"
        />
      </div>
    </Card>
  );
}

function ActiveSourceSection({
  title,
  from,
  to,
  incomeItems,
  expenseItems,
  onOpenBreakdown,
}) {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <Title level={4} className="!mb-0">
          {title}
        </Title>

        <Tooltip title="Задаргаа харах">
          <Tag
            color="blue"
            className="!cursor-pointer !rounded-full !px-3 !py-1"
            onClick={onOpenBreakdown}
          >
            {from} → {to}
          </Tag>
        </Tooltip>
      </div>

      <PieBreakdownCard title="Орлогын задаргаа" items={incomeItems} />
      <PieBreakdownCard title="Зарлагын задаргаа" items={expenseItems} />
    </div>
  );
}

export default function InstitutionWalletWeeklyDashboard({
  institutionName,
  institutionType,
  institutionId,
  data,
  selectedCurrency,
  loading,
  error,
}) {
  const [activeSource, setActiveSource] = useState("report");
  const [selectedTradingHouse, setSelectedTradingHouse] = useState(null);

  const selectedSummary = useMemo(
    () => getSelectedSummary(data, selectedCurrency),
    [data, selectedCurrency]
  );

  if (loading) {
    return (
      <Card className="rounded-[28px] border-0 shadow-md">
        <Skeleton active paragraph={{ rows: 10 }} />
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="rounded-[28px] border-0 shadow-md">
        <Alert
          type="error"
          showIcon
          message="Dashboard"
          description={error}
        />
      </Card>
    );
  }

  if (!data?.currencySummaries?.length) {
    return (
      <Card className="rounded-[28px] border-0 shadow-md">
        <Empty description="Dashboard мэдээлэл алга" />
      </Card>
    );
  }

  if (!selectedSummary) {
    return (
      <Card className="rounded-[28px] border-0 shadow-md">
        <Empty description="Сонгосон валютын dashboard олдсонгүй" />
      </Card>
    );
  }

  const report = selectedSummary.bankReportStatementSummary || {};
  const statement = selectedSummary.bankStatementSummary || {};
  const currency = selectedSummary.currency || selectedCurrency || "-";

  const activeSummary =
    activeSource === "report"
      ? {
          title: "Хийсвэр",
          totalIncome: Number(report.totalIncome || 0),
          totalExpense: Number(report.totalExpense || 0),
          incomeItems: report.incomeBreakdown || [],
          expenseItems: report.expenseBreakdown || [],
        }
      : {
          title: "Ориг",
          totalIncome: Number(statement.totalIncome || 0),
          totalExpense: Number(statement.totalExpense || 0),
          incomeItems: statement.incomeBreakdown || [],
          expenseItems: statement.expenseBreakdown || [],
        };

  const modalItem = {
    institution: {
      id: Number(institutionId),
    },
    selectedCurrency,
  };

  const displayFrom = data?.from || data?.startDate || "-";
  const displayTo = data?.to || data?.endDate || "-";

  return (
    <div className="space-y-6">
      <OverviewSection
        currency={currency}
        activeSource={activeSource}
        onSourceChange={setActiveSource}
        totalIncome={activeSummary.totalIncome}
        totalExpense={activeSummary.totalExpense}
      />

      <ActiveSourceSection
        title={activeSummary.title}
        from={displayFrom}
        to={displayTo}
        incomeItems={activeSummary.incomeItems}
        expenseItems={activeSummary.expenseItems}
        onOpenBreakdown={() => setSelectedTradingHouse(modalItem)}
      />

      <TradingHouseManageModal
        institutionName={institutionName}
        institutionType={institutionType}
        open={!!selectedTradingHouse}
        item={selectedTradingHouse}
        onClose={() => setSelectedTradingHouse(null)}
      />
    </div>
  );
}